import {
  CONNECTIVITY_MODES,
  checkBackendAvailability,
  startConnectivityMonitoring,
  stopConnectivityMonitoring,
  subscribeToConnectivityChanges,
} from '@/services/connectivity/connectivityService';
import { runPendingActionSync } from '@/services/offline/pendingActionSyncEngine';
import { OFFLINE_MODE_ENABLED } from '@/services/offline/offlineFeature';
import { getActiveRestaurantContext } from '@/services/offline/restaurantOfflineData';
import { flushPendingPosPrintJobs } from '@/services/printing/posPrintQueue';

let runtimeStarted = false;
let runtimeEnabled = true;
let lastMode = null;
let runtimeSyncPromise = null;
let lastForegroundCheckAt = 0;

const PRINT_QUEUE_FLUSH_REASONS = new Set(['connectivity-online', 'startup', 'focus', 'visibility-visible']);
const FOREGROUND_CHECK_MIN_INTERVAL_MS = 30000;

function realLocalAuthorityPersisted() {
  try {
    const authority = JSON.parse(
      localStorage.getItem('ewu_operational_authority_v1') || 'null',
    );
    return ['LOCAL_MODE', 'LOCAL_MODE_PREPARING'].includes(authority?.mode);
  } catch {
    return false;
  }
}

async function runRuntimeSync(reason = 'runtime') {
  if (!runtimeEnabled) return null;
  if (realLocalAuthorityPersisted()) return null;
  if (runtimeSyncPromise) {
    return runtimeSyncPromise;
  }

  runtimeSyncPromise = (async () => {
    try {
      await runPendingActionSync();
    } catch (error) {
      console.warn(`EWU pending action sync failed during ${reason}:`, error);
    }

    if (PRINT_QUEUE_FLUSH_REASONS.has(reason)) {
      try {
        const context = await getActiveRestaurantContext();
        if (context?.restaurant_id) {
          flushPendingPosPrintJobs(context.restaurant_id).catch((error) => {
            console.warn(`EWU pending pos print queue flush failed during ${reason}:`, error);
          });
        }
      } catch (error) {
        console.warn(`EWU pending pos print queue context resolution failed during ${reason}:`, error);
      }
    }
  })();

  try {
    return await runtimeSyncPromise;
  } finally {
    runtimeSyncPromise = null;
  }
}

async function verifyBackendAndSync(reason = 'connectivity-online') {
  if (!runtimeEnabled) return;
  if (realLocalAuthorityPersisted()) return;
  if (['focus', 'visibility-visible'].includes(reason)) {
    const now = Date.now();
    if (now - lastForegroundCheckAt < FOREGROUND_CHECK_MIN_INTERVAL_MS) return;
    lastForegroundCheckAt = now;
  }
  try {
    const state = await checkBackendAvailability({ force: true });
    if (state.mode === CONNECTIVITY_MODES.ONLINE) {
      await runRuntimeSync(reason);
    }
  } catch (error) {
    console.warn(`EWU backend availability check failed during ${reason}:`, error);
  }
}

export function startOfflineRuntime() {
  if (!OFFLINE_MODE_ENABLED) return;
  if (runtimeStarted) return;
  runtimeStarted = true;

  if (runtimeEnabled) startConnectivityMonitoring();

  if (typeof window !== 'undefined') {
    window.addEventListener('focus', () => {
      void verifyBackendAndSync('focus');
    });
    window.addEventListener('online', () => {
      void verifyBackendAndSync('connectivity-online');
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void verifyBackendAndSync('visibility-visible');
      }
    });
  }

  subscribeToConnectivityChanges((state) => {
    const becameOnline = state.mode === CONNECTIVITY_MODES.ONLINE
      && lastMode !== CONNECTIVITY_MODES.ONLINE;

    lastMode = state.mode;

    if (state.mode !== CONNECTIVITY_MODES.ONLINE) {
      return;
    }

    if (becameOnline) {
      void runRuntimeSync('connectivity-online');
    }
  });

  void verifyBackendAndSync('startup');
}

export function setOfflineRuntimeEnabled(enabled) {
  runtimeEnabled = OFFLINE_MODE_ENABLED && Boolean(enabled);
  if (!runtimeStarted) return;
  if (!runtimeEnabled) {
    stopConnectivityMonitoring();
    return;
  }
  startConnectivityMonitoring();
  void verifyBackendAndSync('route-enabled');
}
